/**
 * Pure presentation helpers.
 *
 * Kept apart from ui.js so they can be tested without pulling the AWS SDK
 * into the test process.
 */

export function greetingFor(now) {
  const h = now.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * When a recording went up, in the words somebody would use.
 *
 * Relative while that is the more useful answer and a date once it is not.
 * The line this fills used to read "uploaded just now" forever, because the
 * list only ever held uploads made in the open tab — a history needs to tell
 * this morning from last week.
 *
 * A future stamp reads as "just now" rather than "in 3 hours": the clock it
 * is compared against is the phone's, and a phone a few minutes fast is not
 * a thing to report to the student.
 */
export function whenUploaded(uploadedAt, now = new Date()) {
  // A missing stamp is unknown, not the epoch. Coerced to 0 it would render
  // as a date in 1970, which reads as a fact rather than as an absence.
  const stamp = Number(uploadedAt);
  if (uploadedAt == null || !Number.isFinite(stamp)) return '';
  const seconds = Math.round(now.getTime() / 1000 - stamp);
  if (!Number.isFinite(seconds)) return '';
  if (seconds < MINUTE) return 'just now';
  if (seconds < HOUR) {
    const mins = Math.floor(seconds / MINUTE);
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  if (seconds < DAY) {
    const hours = Math.floor(seconds / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (seconds < 7 * DAY) {
    const days = Math.floor(seconds / DAY);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  return new Date(stamp * 1000)
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * The second line of a capture row: how big, which class, and when.
 *
 * Assembled here rather than in ui.js so the wording is testable without a
 * DOM, which is the rule format.js exists for. Parts that are missing are
 * left out rather than rendered as "undefined" — a recording uploaded before
 * the class was known is still a recording.
 */
export function captureMeta(memo, now = new Date()) {
  return [
    memo?.size ? formatBytes(Number(memo.size)) : '',
    memo?.course || memo?.courseName || '',
    whenUploaded(memo?.uploadedAt, now),
  ].filter(Boolean).join(' · ');
}
